import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Divider,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import CheckIcon from '@mui/icons-material/Check';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { useWorkspaces } from '@/lib/queries';
import { useUiStore } from '@/stores/uiStore';

/**
 * Workspace **scope** selector — the only place the active workspace is chosen,
 * which is why no project form or filter offers a second control for it.
 *
 * There is deliberately no "all workspaces" option. A workspace is a boundary,
 * and a view that spans them turns that boundary into a suggestion: totals
 * blend unrelated programmes, and a task created from a cross-workspace list
 * has to guess where it belongs. So the scope is always exactly one.
 *
 * Creating, editing and deleting workspaces lives on /workspaces, so this menu
 * stays a quick switch rather than a management surface.
 */
export function WorkspaceSwitcher({ collapsed }: { collapsed: boolean }) {
  const navigate = useNavigate();
  const { data: workspaces } = useWorkspaces();
  const activeId = useUiStore((s) => s.activeWorkspaceId);
  const setActive = useUiStore((s) => s.setActiveWorkspace);

  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const active = workspaces?.find((workspace) => workspace.id === activeId) ?? null;

  // Falls back to the first workspace rather than to "none": an unset scope
  // used to mean everything, and with that option gone it would mean nothing.
  // This also covers a workspace disappearing (deleted, or access revoked),
  // which must not leave the sidebar pinned to a stale scope.
  useEffect(() => {
    if (!workspaces?.length) return;
    const stillThere = workspaces.some((workspace) => workspace.id === activeId);
    if (!activeId || !stillThere) setActive(workspaces[0].id);
  }, [activeId, workspaces, setActive]);

  return (
    <>
      <Box
        onClick={(event) => setAnchor(event.currentTarget)}
        sx={{
          mx: collapsed ? 0.75 : 1.25,
          mb: 1,
          px: collapsed ? 0.75 : 1.25,
          py: 0.9,
          borderRadius: 2,
          cursor: 'pointer',
          border: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
        }}
      >
        <Box
          sx={{
            width: 22,
            height: 22,
            borderRadius: 1,
            flexShrink: 0,
            bgcolor: active?.color ?? 'text.disabled',
            display: 'grid',
            placeItems: 'center',
            color: '#fff',
            fontSize: 11,
            fontWeight: 800,
          }}
        >
          {(active?.name ?? 'All').slice(0, 1).toUpperCase()}
        </Box>

        {!collapsed && (
          <>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="caption"
                color="text.disabled"
                display="block"
                sx={{ lineHeight: 1.2 }}
              >
                Workspace
              </Typography>
              <Typography variant="body2" noWrap sx={{ fontWeight: 650, lineHeight: 1.2 }}>
                {active?.name ?? '—'}
              </Typography>
            </Box>
            <UnfoldMoreIcon sx={{ fontSize: 17, color: 'text.disabled' }} />
          </>
        )}
      </Box>

      <Menu
        anchorEl={anchor}
        open={!!anchor}
        onClose={() => setAnchor(null)}
        PaperProps={{ sx: { width: 268, borderRadius: 2.5 } }}
      >
        {(workspaces ?? []).map((workspace) => (
          <MenuItem
            key={workspace.id}
            selected={workspace.id === activeId}
            onClick={() => {
              setActive(workspace.id);
              setAnchor(null);
            }}
          >
            <ListItemIcon>
              {workspace.id === activeId ? (
                <CheckIcon fontSize="small" />
              ) : (
                <Box
                  sx={{
                    width: 9,
                    height: 9,
                    borderRadius: '50%',
                    bgcolor: workspace.color,
                    ml: 0.5,
                  }}
                />
              )}
            </ListItemIcon>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Typography variant="body2" noWrap>
                  {workspace.name}
                </Typography>
                {workspace.isPrivate && (
                  <Tooltip title="Private workspace">
                    <LockOutlinedIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
                  </Tooltip>
                )}
              </Stack>
              {workspace.description && (
                <Typography variant="caption" color="text.disabled" noWrap display="block">
                  {workspace.description}
                </Typography>
              )}
            </Box>
          </MenuItem>
        ))}

        <Divider />

        <MenuItem
          onClick={() => {
            navigate('/workspaces');
            setAnchor(null);
          }}
        >
          <ListItemIcon>
            <SettingsOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2">Manage workspaces</Typography>
        </MenuItem>
      </Menu>
    </>
  );
}
